import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Observable, of, throwError } from 'rxjs';
import { map, tap, switchMap, catchError, shareReplay } from 'rxjs/operators';

import { EnvironmentConfigService } from '../../_services/environment-config.service';
import { StorageKeyConstants } from '../../_constants/storage-key-constants';
import {
  MeetingResultDto,
  MeetingListResponseDto
} from './models/meeting-result-dto';

interface MeetingHubAuthResponse {
  token: string;
  expiresIn: number;
  user: unknown;
}

@Injectable({
  providedIn: 'root'
})
export class MeetingHubWidgetService {
  private readonly jwtHelper = new JwtHelperService();

  constructor(
    private httpClient: HttpClient,
    private envConfig: EnvironmentConfigService
  ) {}

  hasToken(): boolean {
    const token = localStorage.getItem(StorageKeyConstants.meetingHubTokenName);
    return !!token && !this.jwtHelper.isTokenExpired(token);
  }

  authenticate(): Observable<boolean> {
    const ccToken = localStorage.getItem(StorageKeyConstants.apiTokenName);
    if (!ccToken) {
      return new Observable((observer) => {
        observer.next(false);
        observer.complete();
      });
    }
    const url = `${this.envConfig.meetingHubApiUrl}/auth`;
    const headers = new HttpHeaders({
      Authorization: `Bearer ${ccToken}`,
      'Content-Type': 'application/json'
    });
    return this.httpClient
      .post<MeetingHubAuthResponse>(url, {}, { headers })
      .pipe(
        tap((response) => {
          if (response?.token) {
            localStorage.setItem(
              StorageKeyConstants.meetingHubTokenName,
              response.token
            );
          }
        }),
        map((response) => !!response?.token)
      );
  }

  // Ensure a valid Meeting Hub token before a data call: reuse the cached token
  // when present, otherwise authenticate. Centralizes the auth dance. (RES-638)
  ensureAuthenticated(): Observable<boolean> {
    return this.hasToken() ? of(true) : this.authenticate();
  }

  getUpcomingMeetings(communityId: number): Observable<MeetingResultDto[]> {
    const url = `${this.envConfig.meetingHubApiUrl}/meetings?communityId=${communityId}&timeFilter=Upcoming`;
    return this.authedGet<MeetingListResponseDto>(url).pipe(
      map((response) => response.items ?? [])
    );
  }

  // Fetch a single meeting by id for the community-calendar popup. The API is
  // community-scoped; a 404 (outside the caller's communities, OR a recurring
  // series master which isn't individually fetchable) is an EXPECTED outcome, so
  // it maps to null here rather than surfacing as an error to every caller.
  getMeetingById(meetingId: string): Observable<MeetingResultDto | null> {
    const url = `${this.envConfig.meetingHubApiUrl}/meetings/${encodeURIComponent(meetingId)}`;
    return this.authedGet<MeetingResultDto>(url).pipe(
      catchError((err) =>
        err?.status === 404 ? of(null) : throwError(() => err)
      )
    );
  }

  // Community meeting-list cache (RES-638). `meetingsInflight` holds a
  // shareReplay'd request per communityId so a prefetch + a near-simultaneous
  // popup open share ONE GET (no duplicate fetch); `meetingsResolved` snapshots
  // the last successful array for the synchronous peek. Entries expire after
  // CACHE_TTL_MS, and clearCache() runs on logout (AuthService) to avoid
  // stale / cross-session data.
  private static readonly CACHE_TTL_MS = 5 * 60 * 1000;
  private readonly meetingsInflight = new Map<
    number,
    { at: number; obs$: Observable<MeetingResultDto[]> }
  >();
  private readonly meetingsResolved = new Map<
    number,
    { at: number; items: MeetingResultDto[] }
  >();
  // Bumped by clearCache() so a request in flight during a clear (e.g. logout)
  // can't repopulate the cache after it. (RES-638 R5 LOW-1)
  private cacheGeneration = 0;

  // List a community's UPCOMING meetings for calendar-popup location resolution.
  // Series occurrences are returned here (each carries location + a masterMeetingId
  // to correlate on) whereas GET /meetings/{id} 404s for the series master.
  // timeFilter=Upcoming keeps the payload small (a long-running daily series has
  // huge history) and matches reality — dial-in / join details are only
  // actionable for upcoming meetings. Past one-time events still resolve via the
  // get-by-id fallback; a past recurring event shows in-person (from the master)
  // without the (moot) online details.
  getCommunityMeetings(communityId: number): Observable<MeetingResultDto[]> {
    const entry = this.meetingsInflight.get(communityId);
    if (entry && Date.now() - entry.at < MeetingHubWidgetService.CACHE_TTL_MS) {
      return entry.obs$;
    }
    const url = `${this.envConfig.meetingHubApiUrl}/meetings?communityId=${communityId}&timeFilter=Upcoming`;
    const generation = this.cacheGeneration;
    const obs$ = this.authedGet<MeetingListResponseDto>(url).pipe(
      map((response) => response.items ?? []),
      tap((items) => {
        // Drop a late response if the cache was cleared (logout) while this
        // request was in flight — otherwise it repopulates after the clear.
        if (generation === this.cacheGeneration) {
          this.meetingsResolved.set(communityId, { at: Date.now(), items });
        }
      }),
      catchError((err) => {
        // Don't cache failures — evict so the next call retries.
        this.meetingsInflight.delete(communityId);
        return throwError(() => err);
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    this.meetingsInflight.set(communityId, { at: Date.now(), obs$ });
    return obs$;
  }

  // Synchronous cache peek (no fetch): lets the popup resolve a one-time meeting
  // instantly when the list was prefetched; undefined on a miss so the caller
  // falls back to a targeted get-by-id instead of fetching the whole list.
  getCachedCommunityMeetings(
    communityId: number
  ): MeetingResultDto[] | undefined {
    const entry = this.meetingsResolved.get(communityId);
    if (
      !entry ||
      Date.now() - entry.at >= MeetingHubWidgetService.CACHE_TTL_MS
    ) {
      return undefined;
    }
    return entry.items;
  }

  // Clear all cached meeting data. Called from AuthService on logout so a later
  // session never serves the previous user's cached meeting details. (RES-638)
  clearCache(): void {
    // Bump the generation first so any in-flight request's late write is ignored.
    this.cacheGeneration += 1;
    this.meetingsInflight.clear();
    this.meetingsResolved.clear();
  }

  private authHeaders(): HttpHeaders {
    const token = localStorage.getItem(StorageKeyConstants.meetingHubTokenName);
    return new HttpHeaders({ Authorization: `Bearer ${token}` });
  }

  // GET with the MH bearer token; on a 401 (expired/invalid token)
  // re-authenticate once and retry, rather than failing the call. (RES-638)
  private authedGet<T>(url: string): Observable<T> {
    return this.httpClient.get<T>(url, { headers: this.authHeaders() }).pipe(
      catchError((err) => {
        if (err?.status === 401) {
          return this.authenticate().pipe(
            switchMap((ok) =>
              ok
                ? this.httpClient.get<T>(url, { headers: this.authHeaders() })
                : throwError(() => err)
            )
          );
        }
        return throwError(() => err);
      })
    );
  }
}
